import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Logger from "../../../../services/Logger";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuMoveHorizontal, LuMoveVertical } from "react-icons/lu";
import AlertDialog from "../../../components/AlertDialog";
import { DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";
import CrossCandEntry from "./CrossCandEntry";
import { generatePositions, getGridWord } from "./CrossCandUtils";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

// Normalize a single letter for comparison (French mode)
const normalizeLetter = (letter, language) => {
    if (!letter || typeof letter !== 'string') return letter;
    if (language === 'french') {
        return normalizeFrenchWord(letter);
    }
    return letter.toLowerCase();
};

export default function CrossCandManual({
    onWordAdd,
    onCheckWordAlreadyPlaced,
    onWordSelect,
    selectedManualWord,
    onShowSnackbar,
    square,
    crossTpl
}) {
    const [manualPattern, setManualPattern] = useState('');
    const [manualRow, setManualRow] = useState(0);
    const [manualCol, setManualCol] = useState(0);
    const [manualDirection, setManualDirection] = useState(DIRECTION_HORIZONTAL);
    const [leftToRight, setLeftToRight] = useState(true);
    const [manualCandidates, setManualCandidates] = useState([]);
    const [manualLoading, setManualLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const updatingWordRef = useRef(false);
    const updatingFormRef = useRef(false);
    const manualChangeRef = useRef(false);
    const updatingCandidatesRef = useRef(false);

    // Alert dialog state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    // Helper function to get direction-specific label
    const getDirectionLabel = () => {
        switch (manualDirection) {
            case DIRECTION_HORIZONTAL:
                return leftToRight ? "Left to Right" : "Right to Left";
            case DIRECTION_VERTICAL:
                return leftToRight ? "Top to Bottom" : "Bottom to Top";
            default:
                return leftToRight ? "Forward" : "Reverse";
        }
    };

    const handleManualPatternSearch = useCallback(async () => {
        if (!manualPattern.trim()) {
            onShowSnackbar("Please enter a pattern", "error");
            return;
        }
        
        if (manualPattern.trim().length <= 3) {
            onShowSnackbar("Pattern must be longer than 3 characters", "error");
            return;
        }

        setManualLoading(true);
        setManualCandidates([]);

        try {
            const candidates = await DictionaryApiService.wildcardSearch(manualPattern, crossTpl?.language || 'english');

            // Transform candidates to include position information for the selected direction
            const transformedCandidates = [];
            candidates.forEach(candidate => {
                // Keep original word with hyphens for display, remove hyphens and spaces for grid placement
                const wordWithHyphens = candidate.word; // Display version (e.g., "ex-voto", "feu de camp")
                const gridWord = getGridWord(candidate.word); // Grid version (e.g., "exvoto", "feudecamp")
                
                // Add normal word (left-to-right/top-to-bottom)
                if (leftToRight) {
                    transformedCandidates.push({
                        word: wordWithHyphens, // Display with hyphens
                        gridWord: gridWord, // Grid version without hyphens
                        score: candidate.score || 0,
                        startRow: manualRow,
                        startCol: manualCol,
                        direction: manualDirection,
                        positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, false),
                        isReversed: false
                    });
                }
                // Add reversed word (right-to-left/bottom-to-top) - only if leftToRight is disabled
                if (!leftToRight) {
                    const reversedGridWord = gridWord.split('').reverse().join('');
                    
                    transformedCandidates.push({
                        word: wordWithHyphens, // Display with hyphens
                        gridWord: gridWord, // Grid version without hyphens
                        score: candidate.score || 0,
                        startRow: manualRow,
                        startCol: manualCol,
                        direction: manualDirection,
                        positions: generatePositions(manualRow, manualCol, manualDirection, reversedGridWord.length, reversedGridWord, true),
                        isReversed: true
                    });
                }
            });

            updatingCandidatesRef.current = true;
            setManualCandidates(transformedCandidates);
            setTimeout(() => {
                updatingCandidatesRef.current = false;
            }, 100);

            if (transformedCandidates.length === 0) {
                onShowSnackbar("No word candidates found for this pattern", "info");
            }
        } catch (error) {
            Logger.error("Error searching for manual candidates:", error);
            onShowSnackbar("Error searching for words. Please try again.", "error");
        } finally {
            setManualLoading(false);
        }
    }, [manualPattern, manualRow, manualCol, manualDirection, leftToRight, onShowSnackbar]);

    // Memoize transformed candidates to prevent unnecessary re-renders
    const memoizedCandidates = useMemo(() => {
        return manualCandidates
            .map(candidate => ({
                ...candidate,
                key: `${candidate.word}-${candidate.startRow}-${candidate.startCol}-${candidate.direction}-${candidate.isReversed}`
            }))
            .sort((a, b) => (b.score || 0) - (a.score || 0)); // Sort by score descending
    }, [manualCandidates]);

    // Pagination constants
    const CANDIDATES_PER_PAGE = 10;
    const totalPages = Math.ceil(memoizedCandidates.length / CANDIDATES_PER_PAGE);
    const startIndex = (currentPage - 1) * CANDIDATES_PER_PAGE;
    const endIndex = startIndex + CANDIDATES_PER_PAGE;
    const paginatedCandidates = memoizedCandidates.slice(startIndex, endIndex);

    // Reset to page 1 when candidates change
    useEffect(() => {
        setCurrentPage(1);
    }, [memoizedCandidates.length]);

    // Pagination handlers
    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    // Auto-search when pattern changes
    useEffect(() => {
        // Skip auto-search if form fields are being updated from a word selection
        if (updatingFormRef.current) {
            return;
        }

        const timeoutId = setTimeout(() => {
            // Double-check the flag in case it changed during the timeout
            if (!updatingFormRef.current && manualPattern.trim().length >= 2) {
                handleManualPatternSearch();
            } else if (!updatingFormRef.current) {
                setManualCandidates([]);
            }
        }, 800); // Increased debounce to 800ms to reduce flickering

        return () => clearTimeout(timeoutId);
    }, [manualPattern, manualRow, manualCol, manualDirection, leftToRight, handleManualPatternSearch]);


    // Update all candidate positions when row/column/direction changes
    useEffect(() => {
        // Skip if form fields are being updated from a word selection or if we're already updating candidates
        // Note: We don't check manualChangeRef here because we want to update candidates even when user manually changes row/col
        if (updatingFormRef.current || updatingCandidatesRef.current) {
            return;
        }

        // Update positions for all existing candidates using functional update
        setManualCandidates(prevCandidates => {
            if (prevCandidates.length === 0) {
                return prevCandidates;
            }
            
            updatingCandidatesRef.current = true;
            const updatedCandidates = prevCandidates.map(candidate => {
                const gridWord = getGridWord(candidate.gridWord || candidate.word);
                return {
                    ...candidate,
                    startRow: manualRow,
                    startCol: manualCol,
                    direction: manualDirection,
                    positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, candidate.isReversed),
                    isReversed: candidate.isReversed
                };
            });
            
            // Reset the flag after a short delay
            setTimeout(() => {
                updatingCandidatesRef.current = false;
            }, 100);
            
            return updatedCandidates;
        });
    }, [manualRow, manualCol, manualDirection, leftToRight]);

    // Update form fields when a new word is selected or position is updated externally
    useEffect(() => {
        // Skip if this is a manual change (user changed form fields directly)
        if (manualChangeRef.current) {
            manualChangeRef.current = false;
            return;
        }
        
        if (selectedManualWord && !updatingWordRef.current) {
            updatingFormRef.current = true;
            const newRow = selectedManualWord.startRow || 0;
            const newCol = selectedManualWord.startCol || 0;
            const rowChanged = newRow !== manualRow;
            const colChanged = newCol !== manualCol;
            
            // Always update form fields to match the selected word's current state
            setManualRow(newRow);
            setManualCol(newCol);
            setManualDirection(selectedManualWord.direction || DIRECTION_HORIZONTAL);
            setLeftToRight(!selectedManualWord.isReversed);
            
            // If row or col changed and we have a pattern, refresh candidates (like clicking RefreshIcon)
            if ((rowChanged || colChanged) && manualPattern.trim().length >= 2) {
                // Trigger a refresh of candidates with new position
                setTimeout(() => {
                    handleManualPatternSearch();
                }, 100);
            }
            
            // Reset the flag after a delay longer than the auto-search debounce to prevent refresh
            setTimeout(() => {
                updatingFormRef.current = false;
            }, 1000);
        }
    }, [selectedManualWord, manualRow, manualCol, manualPattern, handleManualPatternSearch]);

    // Handle manual changes to form fields
    const handleManualRowChange = (value) => {
        const newRow = Math.max(0, Math.min(11, parseInt(value) || 0));
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualRow(newRow);
        if (selectedManualWord) {
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            // Use selectedManualWord's current startCol as source of truth, or fallback to manualCol state
            const currentCol = selectedManualWord.startCol !== undefined ? selectedManualWord.startCol : manualCol;
            const updatedWord = {
                ...selectedManualWord,
                startRow: newRow,
                positions: generatePositions(newRow, currentCol, manualDirection, gridWord.length, gridWord, selectedManualWord.isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    const handleManualColChange = (value) => {
        const newCol = Math.max(0, Math.min(11, parseInt(value) || 0));
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualCol(newCol);
        if (selectedManualWord) {
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            // Use selectedManualWord's current startRow as source of truth, or fallback to manualRow state
            const currentRow = selectedManualWord.startRow !== undefined ? selectedManualWord.startRow : manualRow;
            const updatedWord = {
                ...selectedManualWord,
                startCol: newCol,
                positions: generatePositions(currentRow, newCol, manualDirection, gridWord.length, gridWord, selectedManualWord.isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    const handleDirectionChange = (newDirection) => {
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setManualDirection(newDirection);
        if (selectedManualWord) {
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            const updatedWord = {
                ...selectedManualWord,
                direction: newDirection,
                positions: generatePositions(manualRow, manualCol, newDirection, gridWord.length, gridWord, selectedManualWord.isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    const handleLeftToRightChange = (newLeftToRight) => {
        // Mark this as a manual change to prevent the selectedManualWord useEffect from interfering
        manualChangeRef.current = true;
        // Clear the updatingFormRef flag to allow auto-search to trigger
        updatingFormRef.current = false;
        setLeftToRight(newLeftToRight);
        if (selectedManualWord) {
            const isReversed = !newLeftToRight;
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            const updatedWord = {
                ...selectedManualWord,
                isReversed: isReversed,
                positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    // Verify if the word can be placed at the specified position
    const verifyWordPlacement = (candidate) => {
        if (!square || square.length === 0) {
            return { isValid: false, message: "Puzzle grid is not available" };
        }

        // Use gridWord (without hyphens/spaces) for grid operations, but word for display
        const gridWord = getGridWord(candidate.gridWord || candidate.word).toUpperCase();
        const displayWord = candidate.word; // Keep original with hyphens for error messages
        const positions = candidate.positions;
        const isReversed = candidate.isReversed || false;

        // Check if all positions are within bounds
        for (const pos of positions) {
            if (pos.row < 0 || pos.row >= square.length || pos.col < 0 || pos.col >= square[0].length) {
                return { 
                    isValid: false, 
                    message: `Word "${displayWord}" extends beyond puzzle boundaries at position (${pos.row}, ${pos.col})` 
                };
            }
        }

        // Check if the word matches existing letters in the puzzle
        const language = crossTpl?.language || 'english';
        for (let i = 0; i < gridWord.length; i++) {
            const pos = positions[i];
            const existingLetter = square[pos.row][pos.col];
            
            // When isReversed is true, generatePositions was called with the reversed word
            // So the positions array already has the correct letter order for the reversed placement
            // We should use the value from the position if available, otherwise use the word directly
            // The position's value field contains the correct letter for that position
            let expectedLetter;
            if (pos.value !== undefined && pos.value !== "") {
                // Use the value from the position (which was set correctly by generatePositions)
                expectedLetter = pos.value.toUpperCase();
            } else {
                // Fallback: when isReversed, the word passed to generatePositions was already reversed
                // So position[i] should have word[i] (not reversed again)
                expectedLetter = gridWord[i];
            }
            
            // If there's an existing letter, it must match the expected letter for this position
            // Normalize both letters for comparison when language is French
            if (existingLetter && existingLetter !== "") {
                const normalizedExisting = normalizeLetter(existingLetter, language);
                const normalizedExpected = normalizeLetter(expectedLetter, language);
                
                if (normalizedExisting !== normalizedExpected) {
                    return { 
                        isValid: false, 
                        message: `Word "${displayWord}" conflicts with existing letter "${existingLetter}" at position (${pos.row}, ${pos.col}). Expected "${expectedLetter}"` 
                    };
                }
            }
        }

        return { isValid: true, message: "Word can be placed successfully" };
    };

    const handleManualWordAdd = async (candidate) => {
        // Show "already placed" dialog first if word is duplicated in the grid
        if (onCheckWordAlreadyPlaced && onCheckWordAlreadyPlaced(candidate)) {
            return;
        }
        // Verify word placement before adding
        const verification = verifyWordPlacement(candidate);
        
        if (!verification.isValid) {
            // Show alert dialog with error message
            setAlertMessage(verification.message);
            setAlertOpen(true);
            return;
        }

        const added = await onWordAdd(candidate);
        if (added) {
            setManualPattern('');
            setManualRow(0);
            setManualCol(0);
            setManualDirection(DIRECTION_HORIZONTAL);
            setLeftToRight(true);
            setManualCandidates([]);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, height: '100%', flex: 1 }}>
            {/* Manual Pattern Input Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Pattern"
                        value={manualPattern}
                        onChange={(e) => setManualPattern(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualPattern.trim() && manualPattern.trim().length > 3 && !manualLoading) {
                                handleManualPatternSearch();
                            } else if (e.key === 'Enter' && manualPattern.trim().length <= 3) {
                                onShowSnackbar("Pattern must be longer than 3 characters", "error");
                            }
                        }}
                        placeholder="e.g., ?AT??N or CAT__G"
                        helperText={manualPattern.trim().length > 0 && manualPattern.trim().length <= 3 
                            ? "Pattern must be longer than 3 characters" 
                            : "Use ? for empty cells and letters for existing ones (min 4 characters)"}
                        error={manualPattern.trim().length > 0 && manualPattern.trim().length <= 3}
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <IconButton
                        onClick={handleManualPatternSearch}
                        disabled={!manualPattern.trim() || manualLoading}
                        color="primary"
                        sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:disabled': {
                                borderColor: 'action.disabled'
                            }
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Row"
                        type="number"
                        value={manualRow}
                        onChange={(e) => handleManualRowChange(e.target.value)}
                        slotProps={{ input: { min: 0, max: 11 } }}
                        size="small"
                        sx={{ width: 80 }}
                    />
                    <TextField
                        label="Column"
                        type="number"
                        value={manualCol}
                        onChange={(e) => handleManualColChange(e.target.value)}
                        slotProps={{ input: { min: 0, max: 11 } }}
                        size="small"
                        sx={{ width: 80 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Direction:
                    </Typography>
                    <RadioGroup
                        row
                        value={manualDirection}
                        onChange={(e) => handleDirectionChange(e.target.value)}
                    >
                        <FormControlLabel
                            value={DIRECTION_HORIZONTAL}
                            control={<Radio size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveHorizontal /></Box>}
                        />
                        <FormControlLabel
                            value={DIRECTION_VERTICAL}
                            control={<Radio size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveVertical /></Box>}
                        />
                    </RadioGroup>
                    {crossTpl?.type !== "CROSSWORD" && (
                        <ToggleButtonGroup
                            value={leftToRight ? "leftToRight" : "rightToLeft"}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    handleLeftToRightChange(newValue === "leftToRight");
                                }
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            <ToggleButton value="leftToRight">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Left to Right" :
                                 manualDirection === DIRECTION_VERTICAL ? "Top to Bottom" :
                                 "Forward"}
                            </ToggleButton>
                            <ToggleButton value="rightToLeft">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Right to Left" :
                                 manualDirection === DIRECTION_VERTICAL ? "Bottom to Top" :
                                 "Reverse"}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Box>

            {/* Manual Candidates Section */}
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                        Word Candidates
                    </Typography>
                    {memoizedCandidates.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>
                                <IconButton
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    size="small"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                            </span>
                            <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                                {currentPage} / {totalPages}
                            </Typography>
                            <span>
                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    size="small"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </span>
                        </Box>
                    )}
                </Box>
                {manualLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Typography>Searching for words...</Typography>
                    </Box>
                ) : (
                    <List sx={{ flex: 1, overflow: 'auto' }}>
                        {memoizedCandidates.length === 0 ? (
                            <ListItem>
                                <ListItemText
                                    primary="No candidates found"
                                    secondary="Enter a pattern and click 'Find Words' to search"
                                />
                            </ListItem>
                        ) : (
                            paginatedCandidates.map((candidate) => (
                                <CrossCandEntry
                                    key={candidate.key}
                                    candidate={candidate}
                                    isSelected={selectedManualWord?.word === candidate.word}
                                    onWordSelect={onWordSelect}
                                    onWordAdd={handleManualWordAdd}
                                    mode="manual"
                                />
                            ))
                        )}
                    </List>
                )}
            </Box>

            {/* Alert Dialog */}
            <AlertDialog
                open={alertOpen}
                setOpen={setAlertOpen}
                title="Word Placement Error"
                message={alertMessage}
            />
        </Box>
    );
}

